import { useEffect, useState, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../api";
import { socket } from "../socket";

export function useInAppNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const audioRef = useRef(null);

  // Function to request notification permission
  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return false;
  };

  useEffect(() => {
    // Initialize notification sound
    audioRef.current = new Audio("/sounds/notification.wav");
    audioRef.current.volume = 0.7; // Set volume to 70%

    // Request notification permission on mount
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          console.log("Browser notifications enabled");
        }
      });
    }

    const token = localStorage.getItem("authToken");
    if (!token) return;

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Error decoding JWT:", err);
      return;
    }

    const role = decoded.role;
    const userId = decoded.id;

    // Helper function to register socket with normalized role
    const registerSocket = () => {
      try {
        // Normalize role to lowercase for consistent room matching with backend
        const normalizedRole = role ? role.toLowerCase() : null;
        socket.emit("register", { role: normalizedRole, userId });
        console.log(`🔌 Socket registration sent - Role: ${normalizedRole} (original: ${role}), UserId: ${userId}, SocketId: ${socket.id}`);
      } catch (err) {
        console.error("❌ Error registering socket:", err);
      }
    };

    // ✅ Fetch initial notifications
    api
      .get("/api/in-app-notifications", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setNotifications(res.data.data || []);
        console.log(`📬 Loaded ${res.data.data?.length || 0} initial notifications`);
      })
      .catch((err) => {
        console.error("❌ Error fetching initial notifications:", err);
      });

    api
      .get("/api/in-app-notifications/count", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUnreadCount(res.data.count || 0);
        console.log(`📊 Unread notification count: ${res.data.count || 0}`);
      })
      .catch((err) => {
        console.error("❌ Error fetching notification count:", err);
      });

    // Register immediately if socket is already connected
    if (socket.connected) {
      registerSocket();
    }

    // Handle socket connection events
    socket.on("connect", () => {
      console.log("✅ Socket connected:", socket.id);
      registerSocket();
    });

    // Handle socket disconnection
    socket.on("disconnect", (reason) => {
      console.warn("⚠️ Socket disconnected:", reason);
    });

    // Handle socket reconnection
    socket.on("reconnect", (attemptNumber) => {
      console.log(`🔄 Socket reconnected after ${attemptNumber} attempts`);
      registerSocket();
    });

    // Handle connection errors
    socket.on("connect_error", (error) => {
      console.error("❌ Socket connection error:", error.message);
    });

    socket.on("inAppNotification", (notif) => {
      console.log("📨 Received in-app notification:", {
        Id: notif.Id,
        Type: notif.Type,
        Title: notif.Title,
        TargetRole: notif.TargetRole,
      });
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // Play notification sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0; // Reset sound to start
        audioRef.current.play().catch((err) => {
          console.warn("Could not play notification sound:", err);
        });
      }

      // Show browser notification
      if ("Notification" in window && Notification.permission === "granted") {
        const browserNotif = new Notification(notif.Title || "New Notification", {
          body: notif.Message || "",
          icon: "/favicon.ico", // You can customize this
          badge: "/favicon.ico",
          tag: `notif-${notif.Id}`, // Prevents duplicate notifications
          requireInteraction: false, // Auto-close after a few seconds
          silent: true, // We're playing our own sound
        });

        // Optional: Handle notification click
        browserNotif.onclick = () => {
          window.focus(); // Focus the window
          browserNotif.close();
        };

        // Auto-close after 5 seconds
        setTimeout(() => {
          browserNotif.close();
        }, 5000);
      }
    });

    return () => {
      // Clean up all event listeners
      socket.off("connect");
      socket.off("disconnect");
      socket.off("reconnect");
      socket.off("connect_error");
      socket.off("inAppNotification");
      console.log("🧹 Cleaned up socket event listeners");
    };
  }, []);

  const markAsRead = async (id) => {
    const token = localStorage.getItem("authToken");
    try {
      await api.put(
        `/api/in-app-notifications/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications((prev) =>
        prev.map((n) => (n.Id === id ? { ...n, IsRead: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking read", err);
    }
  };

  const markAllAsRead = async () => {
    const token = localStorage.getItem("authToken");
    try {
      await api.put(
        `/api/in-app-notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications((prev) => prev.map((n) => ({ ...n, IsRead: 1 })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all read", err);
    }
  };

  return { notifications, unreadCount, markAsRead, markAllAsRead, requestNotificationPermission };
}
